import { Stack } from "@mui/material";
import { useTheme } from "design-system/themeContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";

import { IsAllowed } from "../acl";
import { resources } from "../acl/resources";
import { updateUserLanguage } from "../api/functions/all";
import urls from "../api/urls";
import { eventNames, withGAEventTracking } from "../functions/googleAnalytics";
import { ROUTE_PATHS } from "../utils/routerDom";
import { Button } from "./ButtonNew";
import { IconButton } from "./IconButton";
import { Logo } from "./Logo";

const getNavLinks = (pathname) => {
  const addBuildingPath = ROUTE_PATHS.addBuilding();
  const contactPath = ROUTE_PATHS.contact();

  const pageName = addBuildingPath.isActive(pathname)
    ? addBuildingPath.path
    : contactPath.isActive(pathname)
    ? contactPath.path
    : "";

  return [
    {
      eventName: eventNames.NAVBAR_HOME,
      iconName: "BuildingIcon",
      isActive: pageName === "",
      isAllowed: true,
      name: "home",
      to: "/",
    },
    {
      eventName: eventNames.NAVBAR_ADD_BUILDING,
      iconName: "Add",
      isActive: addBuildingPath.isActive(pathname),
      isAllowed: IsAllowed(resources.ADD_BUILDING.VIEW),
      name: addBuildingPath.path,
      reloadDocument: true,
      to: addBuildingPath.navigateUrl,
    },
    {
      eventName: eventNames.NAVBAR_CONTACT,
      iconName: "Contact",
      isActive: false,
      isAllowed: true,
      name: contactPath.path,
      to: urls.user.contactUrl,
    },
  ];
};

const useGetNavLinks = (pathname) => {
  const [navLinks, setNavLinks] = useState(getNavLinks(pathname));
  useEffect(() => {
    setNavLinks(getNavLinks(pathname));
  }, [pathname]);

  return { navLinks, setNavLinks };
};

export const Navbar = ({ className }) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const alternativeLanguage = i18n.language === "en" ? "de" : "en";
  const { user } = useSelector((store) => store.user);
  const { pathname } = useLocation();
  const { navLinks, setNavLinks } = useGetNavLinks(pathname);

  const changeLink = (clickedNavLink) => {
    if (clickedNavLink.name === ROUTE_PATHS.contact().path) return;
    const newNavlinks = navLinks?.map((navLink) => {
      if (navLink.name === clickedNavLink.name) return { ...navLink, isActive: true };
      else return { ...navLink, isActive: false };
    });
    setNavLinks(newNavlinks);
  };

  const navLinkProps = (navLink) => ({
    child: {
      buttonProps: {
        height: "4rem",
        isActive: navLink.isActive,
        size: "sm",
        variant: "tertiary",
        width: "4rem",
      },
      iconProps: {
        iconName: navLink.iconName,
        style: { transform: "scale(1.25)" },
      },
    },
    parent: {
      onClick: () => withGAEventTracking(navLink.eventName, {}, () => changeLink(navLink)),
      reloadDocument: !!navLink.reloadDocument,
      tabIndex: -1,
      to: navLink.to,
    },
  });

  return (
    <nav className={`${className} flex flex-col items-center bg-lds-surface-bright px-1.5 py-6 shadow`}>
      <Stack className="h-20vh min-h-55 justify-between " flexDirection="column">
        {navLinks.map((navLink, index) => {
          return (
            <>
              {navLink.isAllowed && (
                <NavLink key={navLink.name} {...navLinkProps(navLink).parent}>
                  <IconButton {...navLinkProps(navLink).child} />
                </NavLink>
              )}
              {index === 1 && navLinks.length > 2 && <div className="w-4/5 border-ds-subtle" />}
            </>
          );
        })}
        <Button
          className="!uppercase"
          onClick={() => {
            i18n.changeLanguage(alternativeLanguage);
            localStorage.setItem("language", alternativeLanguage);
            updateUserLanguage(user.user_id, alternativeLanguage);
          }}
          size="md"
          variant="tertiary"
        >
          {alternativeLanguage}
        </Button>
      </Stack>
      <img alt="Background" className="h-28vh absolute bottom-0 w-full" src={theme.navLooper.src} />
      <Logo className="absolute bottom-6 z-50 max-w-[4rem]" variant="sidebar" />
    </nav>
  );
};
